/** Copyright 2026 Midas Healthcare Solutions - All Rights Reserved **/
import { SortCriteriaItem } from './base.types';
import { MedicationV2 } from './formulary.model';
import { Flagging, FlagType, Wasting, WorkFlowStatus } from './wasting.model';
import { InterruptedState, IWorkflow } from './workflow.model';

export interface Sample {
  id: number;
  wasting: Wasting;
  wastingId: number | null;
  medication?: MedicationV2;
  sampleIdentifier: string;
  isMissing: boolean;
  collectionOrder: number | null;
  finalWasting: Wasting | null;
  manuallyAdded: boolean;
}

export interface Transport extends IWorkflow {
  transportIdentifier: string | null;
  originalTransportIdentifier: string | null;
  originalTransportId: number | null;
  locationId: number;
  locationName: string;
  transportUserId: number;
  transportUserName: string;
  transportWitnessUserId: number | null;
  transportWitnessUserName: string | null;
  samples: Sample[];
  transportCollectionTime: string;
  originalTransport?: Transport | null;
  cartName: string | null;
  finalWastingStatus: WorkFlowStatus | null;
  finalInterruptedState: InterruptedState | null;
  screenVideoUploadComplete: boolean;
  witnessVideoMetadata: string | null;
  flaggings: Flagging[];

  // lightweight transport summary
  numberOfSamples: number;
  numberOfUnwitnessedWastes: number;
  numberOfFlaggedWastes: number;
  numberOfMissingSamples: number;
}

export enum TransportsSortCriteria {
  Date,
  Location,
  Cart,
  User,
  Samples,
}

export type TransportsSortCriteriaItem = SortCriteriaItem<typeof TransportsSortCriteria>;

export interface QueryTransportsRequest {
  numberOfRecords: number;
  startingIndex: number;
  searchString?: string;
  locations?: number[];
  carts?: number[];
  users?: number[];
  transports?: number[];
  startDate?: string | null;
  endDate?: string | null;
  sortCriteria?: TransportsSortCriteriaItem[];
  statuses: WorkFlowStatus[];
  showUserTransports?: boolean;
  showOnlyStaleTransports?: boolean;
  sortByDateOldestFirst?: boolean;
  flagTypes?: FlagType[];
  includeActiveFlagTransports?: boolean;
  finalWasteCompleted?: boolean;
  onlyResolve?: boolean;

  // For Alibi display, do not update count numbers
  silentUpdate?: boolean;
}

export enum TransportsPageType {
  AnytimeWitness,
  ReviewRequired,
  HistoricalWastes,
  TransportBags,
  AssayBags,
  BlueDiscrepancyBags,
  FlaggedTransports,
  FlaggedBySystem,
  FlaggedByAnytimeWitness,
}

export enum UiTransportView {
  Detail,
  Pharmacy,
  PharmacyDetail,
  Assay,
  BlueDiscrepancyBags,
}

export function getUnwitnessedWastes(transport: Transport): number {
  return transport.samples.length
    ? transport.samples.filter(
        (s) => s.wasting?.workflowStatus === WorkFlowStatus.Pending,
      ).length
    : (transport.numberOfUnwitnessedWastes ?? 0);
}

export function getFlaggedWastes(transport: Transport): number {
  return transport.samples.length
    ? transport.samples.filter((s) =>
        s.wasting?.flaggings.some((f) => !f.flagCleared),
      ).length
    : (transport.numberOfFlaggedWastes ?? 0);
}

export function getMissingSamples(transport: Transport): number {
  return transport.samples.length
    ? transport.samples.filter((s) => s.isMissing).length
    : (transport.numberOfMissingSamples ?? 0);
}

export function getNumberOfSamples(transport: Transport): number {
  return transport.samples.length || transport.numberOfSamples || 0;
}
